import { type ReactNode, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { useBlocker } from '@tanstack/react-router';
import {
  FontWeight,
  Heading,
  Text,
  type LiliPasswordInputHandle,
} from '@lili/lili-frontend-common';
import { PasswordInput } from '@Components/inputs/PasswordInput';
import { CurrentDate } from '@Components/CurrentDate/CurrentDate';
import { StepPageWrapper } from '@Layout/StepPageWrapper';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import {
  LILI_TERMS_OF_USE_URL,
  LILI_PRIVACY_POLICY_URL,
  SUNRISE_ACCOUNT_MANAGMENT_AGREEMENT_URL,
  SUNRISE_VISA_SAVINGS_EDGE_TERMS_URL,
} from '@Types/consts';
import { useSetPasswordLogic } from './hooks/useSetPasswordLogic';

// https://github.com/i18next/react-i18next/issues/1747
const LinkToAgreement = ({ children, href }: { children?: ReactNode; href: string }) => {
  return (
    <a
      href={href}
      target='_blank'
      rel='noreferrer'
      className='underline text-text-brand-primary hover:text-text-brand-primary-hover'>
      {children}
    </a>
  );
};

export const SetPassword = () => {
  const { t } = useTranslation('inviteTeamMember');
  const passwordInputRef = useRef<LiliPasswordInputHandle>(null);
  const {
    formMethods,
    handleSubmit,
    isSettingPassword,
    isCompletingOnboarding,
    isSendingConsent,
    isKyc,
    authorizationTitle,
    authorizationTextKey,
  } = useSetPasswordLogic();

  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (
        next.routeId === routesModel[RoutesMap.ACCOUNTANT_PORTAL_COMPLETE].path ||
        next.routeId === routesModel[RoutesMap.INVITE_TEAM_MEMBER_FINISHED].path ||
        next.routeId === routesModel[RoutesMap.AUTH_USER_IDENTITY].path
      ) {
        return false;
      }
      return true;
    },
  });

  return (
    <FormProvider {...formMethods}>
      <StepPageWrapper
        heading={t('setPassword.title')}
        formMethods={formMethods}
        handleSubmit={() => {
          // Re-mask the password before submitting so it never appears in the consent (POA) snapshot.
          passwordInputRef.current?.hidePassword();
          return handleSubmit(formMethods.getValues());
        }}
        isLoading={isSettingPassword || isCompletingOnboarding || isSendingConsent}
        footerInputSlot={<></>}
        customSubmitTitle={t('setPassword.submit')}
        shouldStrechCta
        showBackButton={false}>
        <PasswordInput ref={passwordInputRef} />
        <div className='mb-[24px]'>
          <CurrentDate label={t('accountantPersonalDetails.currentDate')} className='mb-[24px]' />

          <div className='bg-surface-primary-bw-01 rounded-[12px] p-[16px] outline outline-1 outline-outline-primary-bw-03'>
            <Heading.H9 fontWeight={FontWeight.MEDIUM} className='mb-[4px]'>
              {authorizationTitle}
            </Heading.H9>
            <Text.H10 className='text-text-main-secondary whitespace-pre-line'>
              <Trans
                t={t}
                i18nKey={authorizationTextKey}
                components={{
                  CustomLink: (
                    <LinkToAgreement href='https://lili.co/legal-documents/sunrise-account-agreement' />
                  ),
                }}
              />
            </Text.H10>
          </div>
        </div>
        {isKyc && (
          <Text.H10 className='mt-[16px] pb-[24px] text-text-main-secondary'>
            <Trans
              ns='inviteTeamMember'
              i18nKey='setPassword.termsText'
              components={{
                termsLink: (
                  <a
                    href={LILI_TERMS_OF_USE_URL}
                    className='text-text-brand-primary underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
                sunriseLink: (
                  <a
                    href={SUNRISE_ACCOUNT_MANAGMENT_AGREEMENT_URL}
                    className='text-text-brand-primary underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
                visaLink: (
                  <a
                    href={SUNRISE_VISA_SAVINGS_EDGE_TERMS_URL}
                    className='text-text-brand-primary underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
                privacyLink: (
                  <a
                    href={LILI_PRIVACY_POLICY_URL}
                    className='text-text-brand-primary underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
              }}
            />
          </Text.H10>
        )}
      </StepPageWrapper>
    </FormProvider>
  );
};
